import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { withUnit } from '../../utils/formatUnits';

// Blocking production checkpoint: every 2h (and at midnight) the operator must confirm how many
// complete spools of the item in progress really exist. Rendered by Layout for anyone logged in
// on a production line; nothing else is usable until every pending checkpoint is answered.

interface CheckpointItem {
  material: string | null;
  kolor: string | null;
  srednica: string | null;
  waga: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  indeks_kat: string | null;
  do_produkcji: number;
  current_total: number;
}

interface PendingCheckpoint {
  id: number;
  kind: 'periodic' | 'daily' | 'final';
  requested_at: string;
  requested_at_local: string;
  waiting_seconds: number;
  shift: string | null;
  production_item_id: number;
  /** Not null = control mode: the winder counted this many spools, operator confirms or corrects. */
  system_quantity: number | null;
  item: CheckpointItem | null;
}

const POLL_MS = 60_000;

const CheckpointModal: React.FC = () => {
  const { user, selectedLine } = useAuth();
  const { t } = useLanguage();
  const [pending, setPending] = useState<PendingCheckpoint[]>([]);
  const [lateMinutes, setLateMinutes] = useState(15);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the typed number is LOWER than what the system already has — the operator must
  // confirm they really mean a correction downwards, not "how many since last time".
  const [confirmDecrease, setConfirmDecrease] = useState<number | null>(null);
  // Control mode: operator pressed "NO" and now types the real count.
  const [correcting, setCorrecting] = useState(false);

  const lineId = selectedLine?.id ?? null;
  // Only line operators are blocked — managers/admin/packaging/tracker may carry a stale
  // selectedLine in localStorage and must never be locked out of their screens.
  const enabled = !!user && user.role === 'user' && !!lineId;

  const fetchPending = useCallback(async () => {
    if (!enabled || !lineId) return;
    try {
      const res = await api.get('/checkpoints/pending', { params: { line_id: lineId } });
      setPending(res.data.checkpoints ?? []);
      setLateMinutes(res.data.late_minutes ?? 15);
    } catch {
      // keep whatever we had — a transient error must not unblock or double-block the line
    }
  }, [enabled, lineId]);

  useEffect(() => {
    if (!enabled) return;
    fetchPending();
    const timer = setInterval(fetchPending, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, fetchPending]);

  const current = pending[0];

  useEffect(() => {
    // Pre-fill with the last known total so a "nothing changed" answer is one tap away.
    setValue(current?.item ? String(current.item.current_total) : '');
    setError(null);
    setConfirmDecrease(null);
    setCorrecting(false);
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled || !current) return null;

  const isLate = current.waiting_seconds > lateMinutes * 60;
  const waitingMin = Math.floor(current.waiting_seconds / 60);
  const item = current.item;
  const colour = item?.kolor?.includes('\n') ? item.kolor.split('\n').join('/') : item?.kolor;
  const productName = [item?.material, colour, withUnit(item?.srednica, 'mm'), withUnit(item?.waga, 'kg')]
    .filter(Boolean).join(' ');

  const controlMode = current.system_quantity !== null && current.system_quantity !== undefined;

  const submitExact = async (qty: number) => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/checkpoints/${current.id}/answer`, { quantity: qty });
      setPending(prev => prev.filter(c => c.id !== current.id));
      fetchPending();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t('checkpoint.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (force = false) => {
    const qty = parseInt(value, 10);
    if (Number.isNaN(qty) || qty < 0) {
      setError(t('checkpoint.invalidQuantity'));
      return;
    }
    const systemTotal = item?.current_total ?? 0;
    if (!force && qty < systemTotal) {
      setConfirmDecrease(systemTotal - qty);
      return;
    }
    setConfirmDecrease(null);
    setSaving(true);
    setError(null);
    try {
      await api.post(`/checkpoints/${current.id}/answer`, { quantity: qty });
      setPending(prev => prev.filter(c => c.id !== current.id));
      // Refresh right away — another checkpoint may already be due.
      fetchPending();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t('checkpoint.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.85)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 32, maxWidth: 560, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)', fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <span style={{ fontSize: 34 }}>{current.kind === 'daily' ? '🕛' : '⏱️'}</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#667eea', letterSpacing: 1, textTransform: 'uppercase' }}>
              {t('checkpoint.badge')} · {selectedLine?.name}
            </div>
            <h2 style={{ margin: 0, fontSize: 22, color: '#1e293b' }}>
              {current.kind === 'daily' ? t('checkpoint.dailyTitle') : t('checkpoint.periodicTitle')}
            </h2>
          </div>
          {pending.length > 1 && (
            <span style={{ marginLeft: 'auto', background: '#eef2ff', color: '#667eea', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
              {t('checkpoint.remaining', { count: pending.length })}
            </span>
          )}
        </div>

        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', margin: '16px 0',
        }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>{productName || item?.indeks_kat || '—'}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {item?.indeks_kat && <span>{t('checkpoint.index')}: <strong>{item.indeks_kat}</strong> · </span>}
            {item?.marka && <span>{item.marka} · </span>}
            {t('checkpoint.plan')}: <strong>{item?.do_produkcji ?? '—'}</strong> {t('checkpoint.pcs')}
            {' · '}{t('checkpoint.systemTotal')}: <strong>{item?.current_total ?? 0}</strong> {t('checkpoint.pcs')}
          </div>
        </div>

        {controlMode && !correcting && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '14px 16px', textAlign: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#4338ca', fontWeight: 600 }}>{t('checkpoint.systemCounted')}</div>
              <div style={{ fontSize: 40, fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{current.system_quantity} <span style={{ fontSize: 16, fontWeight: 600, color: '#64748b' }}>{t('checkpoint.pcs')}</span></div>
            </div>
            <p style={{ margin: '0 0 12px 0', fontSize: 16, color: '#1e293b', fontWeight: 600, textAlign: 'center' }}>
              {t('checkpoint.controlQuestion', { count: current.system_quantity ?? 0 })}
            </p>
            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => submitExact(current.system_quantity ?? 0)}
                disabled={saving}
                style={{ flex: 1, padding: '16px 0', background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, fontSize: 18, fontWeight: 800, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
              >
                {saving ? '...' : `✔ ${t('checkpoint.yes')}`}
              </button>
              <button
                onClick={() => { setCorrecting(true); setValue(String(current.system_quantity ?? '')); }}
                disabled={saving}
                style={{ flex: 1, padding: '16px 0', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 18, fontWeight: 700, cursor: 'pointer' }}
              >
                ✕ {t('checkpoint.no')}
              </button>
            </div>
          </div>
        )}

        {(!controlMode || correcting) && (<>
        <p style={{ margin: '0 0 6px 0', fontSize: 16, color: '#1e293b', fontWeight: 600 }}>
          {correcting ? t('checkpoint.correctQuestion') : current.kind === 'daily'
            ? t('checkpoint.dailyQuestion', { time: current.requested_at_local.slice(11) })
            : t('checkpoint.periodicQuestion')}
        </p>
        {(current.kind === 'daily' || isLate) && (
          <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 12px' }}>
            ⚠ {t('checkpoint.exactMomentHint', { time: current.requested_at_local })}
            {isLate && <> · {t('checkpoint.lateHint', { minutes: waitingMin })}</>}
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '12px 0 16px' }}>
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={value}
            autoFocus
            onChange={(e) => { setValue(e.target.value); setConfirmDecrease(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !saving && confirmDecrease === null) submit(); }}
            style={{
              flex: 1, padding: '16px 18px', fontSize: 28, fontWeight: 800, textAlign: 'center',
              border: `2px solid ${error ? '#dc3545' : '#667eea'}`, borderRadius: 10, color: '#1e293b',
            }}
          />
          <span style={{ fontSize: 18, fontWeight: 700, color: '#475569' }}>{t('checkpoint.pcs')}</span>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>
            {error}
          </div>
        )}

        {confirmDecrease !== null && (
          <div style={{ background: '#fef2f2', border: '2px solid #dc3545', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>
              ⚠ {t('checkpoint.decreaseTitle', { system: item?.current_total ?? 0, entered: value })}
            </div>
            <div style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 12 }}>
              {t('checkpoint.decreaseHint', { delta: confirmDecrease })}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmDecrease(null)}
                style={{ flex: 1, padding: '12px 0', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
              >
                ↩ {t('checkpoint.decreaseFix')}
              </button>
              <button
                onClick={() => submit(true)}
                disabled={saving}
                style={{ flex: 1, padding: '12px 0', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
              >
                {saving ? '...' : t('checkpoint.decreaseConfirm', { delta: confirmDecrease })}
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => submit()}
          disabled={saving || confirmDecrease !== null}
          style={{
            width: '100%', padding: '16px 0', background: '#667eea', color: '#fff', border: 'none',
            borderRadius: 10, fontSize: 18, fontWeight: 800, cursor: 'pointer', opacity: saving || confirmDecrease !== null ? 0.5 : 1,
          }}
        >
          {saving ? '...' : `✔ ${t('checkpoint.confirm')}`}
        </button>
        </>)}
        <p style={{ margin: '12px 0 0 0', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
          {t('checkpoint.blockingHint')}
        </p>
      </div>
    </div>
  );
};

export default CheckpointModal;
